"use client";

import * as React from "react";
import { useTranslations } from "next-intl";
import { Skeleton } from "@/components/ui/skeleton";
import LowRiskIcon from "../icons/generated/LowRiskIcon";
import { MediumRiskIcon } from "../icons/generated/MediumRiskIcon";
import { HighRiskIcon } from "../icons/generated/HighRiskIcon";
import { CriticalRiskIcon } from "../icons/generated/CriticalRiskIcon";
import { RefreshIcon } from "../icons";
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from "recharts";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { getCredibilityColor } from "@/app/assignments/[id]/utils/credibilityColor";

type RiskLevelCardProps = {
  score?: number;
  isLoading?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  showTrustIndex?: boolean;
  disableAnimation?: boolean;
  /** Показывать иконку робота слева. До генерации AI-отчёта — только рамка без робота. */
  showIcon?: boolean;
};

type RiskLevel = "low" | "medium" | "high" | "critical";

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function getRiskLevel(score: number): RiskLevel {
  if (score >= 80) return "low";
  if (score >= 60) return "medium";
  if (score >= 40) return "high";
  return "critical";
}

function getRiskConfig(scoreRaw: number | undefined, t: (key: string) => string) {
  // score is optional in props and may also come from API as NaN/Infinity.
  // Normalize to a finite number before clamping.
  const normalizedScore =
    typeof scoreRaw === "number" && Number.isFinite(scoreRaw) ? scoreRaw : 0;

  const score = clamp(normalizedScore, 0, 100);
  const level = getRiskLevel(score);

  const colorClassByLevel: Record<RiskLevel, string> = {
    low: "text-[#91C000] border-[#91C000]",
    medium: "text-[#EBB518] border-[#EBB518]",
    high: "text-[#F97101] border-[#F97101]",
    critical: "text-[#D15B46] border-[#D15B46]",
  };

  // ✅ Use the same border color for the icon wrapper
  const iconBorderClassByLevel: Record<RiskLevel, string> = {
    low: "border-[#91C000]",
    medium: "border-[#EBB518]",
    high: "border-[#F97101]",
    critical: "border-[#D15B46]",
  };

  const labelByLevel: Record<RiskLevel, string> = {
    low: t("label-risk-level-low"),
    medium: t("label-risk-level-medium"),
    high: t("label-risk-level-high"),
    critical: t("label-risk-level-critical"),
  };

  const descriptionByLevel: Record<RiskLevel, string> = {
    low: t("label-risk-description-low"),
    medium: t("label-risk-description-medium"),
    high: t("label-risk-description-high"),
    critical: t("label-risk-description-critical"),
  };

  const IconByLevel: Record<RiskLevel, React.ComponentType<any>> = {
    low: LowRiskIcon,
    medium: MediumRiskIcon,
    high: HighRiskIcon,
    critical: CriticalRiskIcon,
  };

  return {
    score,
    level,
    label: labelByLevel[level],
    description: descriptionByLevel[level],
    cardClass: colorClassByLevel[level],
    iconBorderClass: iconBorderClassByLevel[level],
    Icon: IconByLevel[level],
  };
}

function RiskLevelCardSkeleton() {
  return (
    <div className="flex flex-row items-center justify-between w-full h-[60px] pr-[10px] border rounded-tl-[50px] rounded-tr-[6px] rounded-br-[6px] rounded-bl-[50px] border-gray-200">
      <div className="flex flex-row items-center justify-center gap-[20px]">
        <div className="h-[59.88px] rounded-[50px] p-[1px] border border-gray-200 flex items-center justify-center">
          <div className="h-full rounded-[50px] bg-white flex items-center justify-center p-[5px]">
            <Skeleton className="w-8 h-8 rounded-full" />
          </div>
        </div>

        <div className="flex flex-col gap-[10px]">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      <Skeleton className="w-9 h-9 rounded" />
    </div>
  );
}

export default function RiskLevelCard({
  score,
  isLoading,
  onRefresh,
  isRefreshing,
  showTrustIndex = false,
  disableAnimation = false,
  showIcon = true,
}: RiskLevelCardProps) {
  const t = useTranslations();
  const cfg = React.useMemo(() => getRiskConfig(score, t), [score, t]);
  const Icon = cfg.Icon;
  const credibility = typeof score === "number" ? score : 0;
  const { rgb } = getCredibilityColor(credibility);
  const isZeroCredibility = credibility === 0;
  const displayValue = credibility < 0 ? 0 : credibility;

  const chartData = [
    {
      name: "credibility",
      value: isZeroCredibility ? 100 : displayValue,
      fill: rgb,
    },
  ];

  const chartConfig = {
    value: {
      label: "Credibility",
    },
  } satisfies ChartConfig;

  if (isLoading) return <RiskLevelCardSkeleton />;

  return (
    <div
      className={`
        flex flex-row items-center justify-between
        w-full h-[60px]
        pr-[10px]
        border
        ${
          showIcon
            ? "rounded-tl-[50px] rounded-tr-[6px] rounded-br-[6px] rounded-bl-[50px]"
            : "rounded-[6px]"
        }
        ${cfg.cardClass}
      `}
    >
      <div className={`flex flex-row items-center justify-center gap-[20px] ${showIcon ? "" : "pl-[24px]"}`}>
        {showIcon && (
          <div
            className={`
            h-[59.88px] rounded-[50px] p-[1px]
            border ${cfg.iconBorderClass}
            flex items-center justify-center
          `}
          >
            <div className="h-full rounded-[50px] bg-white flex items-center justify-center p-[5px]">
              <Icon aria-hidden />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-[10px]">
          <p className="font-inter font-semibold text-[14px] leading-[1] tracking-normal">
            {cfg.label}
          </p>
          <p className="font-inter font-normal text-[12px] leading-[1] tracking-normal">
            {cfg.description}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {showTrustIndex && (
          <div className="h-9 w-9 flex items-center justify-center">
            <ChartContainer config={chartConfig} className="aspect-square h-full w-full">
              <RadialBarChart
                data={chartData}
                startAngle={90}
                endAngle={
                  90 +
                  (isZeroCredibility
                    ? 360
                    : credibility < 0
                    ? 0
                    : (credibility / 100) * 360)
                }
                innerRadius={14}
                outerRadius={20}
              >
                <PolarGrid
                  gridType="circle"
                  radialLines={false}
                  stroke="none"
                  className="first:fill-muted last:fill-background"
                  polarRadius={[16, 12]}
                />
                <RadialBar dataKey="value" background cornerRadius={10} isAnimationActive={!disableAnimation} />
                <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                        return (
                          <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                            <tspan
                              dy="0.32em"
                              className={`text-[9px] font-bold ${
                                credibility < 0 ? "fill-foreground" : ""
                              }`}
                              style={credibility >= 0 ? { fill: rgb } : undefined}
                            >
                              {credibility < 0 ? "—" : credibility}
                            </tspan>
                          </text>
                        );
                      }
                    }}
                  />
                </PolarRadiusAxis>
              </RadialBarChart>
            </ChartContainer>
          </div>
        )}
      </div>
    </div>
  );
}
